import { useState, useEffect, useRef } from 'react';
import type { AdminLive, LiveStatus, LiveProvider } from '@/mocks/adminLive';
import { statusLabels, statusColors, providerLabels, providerIcons, providerColors } from '@/mocks/adminLive';
import { liveSessionStatusColor } from '@/types/liveSession';
import type { LiveSessionState } from '../page';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { Link } from 'react-router-dom';

interface LiveGridProps {
  lives: AdminLive[];
  filterStatus: LiveStatus | 'todos';
  onChangeStatus: (st: LiveStatus | 'todos') => void;
  filterProvider: LiveProvider | 'todos';
  onChangeProvider: (p: LiveProvider | 'todos') => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onEdit: (live: AdminLive) => void;
  onDelete: (live: AdminLive) => void;
  onFinalizar: (live: AdminLive) => void;
  onCopyLink: (live: AdminLive) => void;
  onIniciar: (live: AdminLive) => void;
  isPremium: boolean;
  liveSessionStates?: Record<number, LiveSessionState>;
}

const allProviders: LiveProvider[] = ['youtube', 'facebook', 'vps', 'mux'];

export default function LiveGrid({
  lives,
  filterStatus,
  onChangeStatus,
  filterProvider,
  onChangeProvider,
  searchQuery,
  onSearchChange,
  onEdit,
  onDelete,
  onFinalizar,
  onCopyLink,
  onIniciar,
  isPremium,
  liveSessionStates,
}: LiveGridProps) {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col gap-3">
        {/* Search */}
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar lives por título, descripción o locutor..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-circle-fill text-sm"></i>
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Estado</span>
          {(['todos', 'en_vivo', 'scheduled', 'finalizado', 'recording_available', 'archived'] as const).map((st) => {
            const count = st === 'todos' ? lives.length : lives.filter((l) => l.status === st).length;
            const isActive = filterStatus === st;
            const stColor = st !== 'todos' ? statusColors[st] : null;
            return (
              <button
                key={st}
                onClick={() => onChangeStatus(st)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap flex items-center gap-1.5 ${
                  isActive
                    ? st === 'en_vivo'
                      ? 'bg-red-500 text-background-50'
                      : 'bg-foreground-900 text-background-50'
                    : 'bg-background-50 border border-background-200 text-foreground-500 hover:bg-background-200'
                }`}
              >
                {st !== 'todos' && (
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      isActive ? 'bg-background-50' : stColor?.dot
                    } ${st === 'en_vivo' && !isActive ? 'animate-pulse' : ''}`}
                  ></span>
                )}
                {st === 'todos' ? 'Todos' : statusLabels[st]} ({count})
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Proveedor</span>
          {(['todos', ...allProviders] as const).map((p) => {
            const count = p === 'todos' ? lives.length : lives.filter((l) => l.provider === p).length;
            return (
              <button
                key={p}
                onClick={() => onChangeProvider(p)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1 ${
                  filterProvider === p
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-50 border border-background-200 text-foreground-400 hover:bg-background-200'
                }`}
              >
                {p !== 'todos' && <i className={`${providerIcons[p]} text-[10px]`}></i>}
                {p === 'todos' ? 'Todos' : providerLabels[p]} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Results count */}
      <p className="text-xs text-foreground-400">
        Mostrando {lives.length} de {lives.length} lives
      </p>

      {/* Empty state */}
      {lives.length === 0 && (
        <div className="bg-background-50 rounded-2xl border border-background-200 p-12 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-live-line text-foreground-300 text-2xl"></i>
          </div>
          <h4 className="text-sm font-bold text-foreground-900 mb-1">Sin resultados</h4>
          <p className="text-xs text-foreground-400 leading-relaxed max-w-xs mx-auto">
            No se encontraron lives con los filtros aplicados. Probá limpiando los filtros o ajustando la búsqueda.
          </p>
          <button
            onClick={() => {
              onChangeStatus('todos');
              onChangeProvider('todos');
              onSearchChange('');
            }}
            className="mt-4 inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold hover:bg-background-200 cursor-pointer transition-colors"
          >
            <i className="ri-filter-off-line text-sm"></i>
            Limpiar filtros
          </button>
        </div>
      )}

      {/* Desktop grid */}
      <div className="hidden md:grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {lives.map((live) => (
          <LiveCardDesktop
            key={live.id}
            live={live}
            onEdit={onEdit}
            onDelete={onDelete}
            onFinalizar={onFinalizar}
            onCopyLink={onCopyLink}
            onIniciar={onIniciar}
            isPremium={isPremium}
            liveSessionStates={liveSessionStates}
          />
        ))}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {lives.map((live) => (
          <LiveCardMobile
            key={live.id}
            live={live}
            onEdit={onEdit}
            onDelete={onDelete}
            onFinalizar={onFinalizar}
            onCopyLink={onCopyLink}
            onIniciar={onIniciar}
            isPremium={isPremium}
            liveSessionStates={liveSessionStates}
          />
        ))}
      </div>
    </div>
  );
}

/* ─── Timer hook ─── */
function useElapsedTimer(startedAt: string | null): string {
  const [elapsed, setElapsed] = useState('00:00:00');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!startedAt) {
      setElapsed('00:00:00');
      return () => {};
    }

    function tick() {
      const diff = Date.now() - new Date(startedAt!).getTime();
      if (diff < 0) {
        setElapsed('00:00:00');
        return;
      }
      const totalSec = Math.floor(diff / 1000);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      setElapsed(
        `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`,
      );
    }

    tick();
    intervalRef.current = setInterval(tick, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [startedAt]);

  return elapsed;
}

/* ─── Desktop Card ─── */
function LiveCardDesktop({
  live,
  onEdit,
  onDelete,
  onFinalizar,
  onCopyLink,
  onIniciar,
  isPremium,
  liveSessionStates,
}: {
  live: AdminLive;
  onEdit: (l: AdminLive) => void;
  onDelete: (l: AdminLive) => void;
  onFinalizar: (l: AdminLive) => void;
  onCopyLink: (l: AdminLive) => void;
  onIniciar: (l: AdminLive) => void;
  isPremium: boolean;
  liveSessionStates?: Record<number, LiveSessionState>;
}) {
  const stColor = statusColors[live.status];
  const provColor = providerColors[live.provider];
  const session = liveSessionStates?.[live.id];
  const sessionColor = session ? liveSessionStatusColor(session.status) : null;
  const elapsed = useElapsedTimer(session?.startedAt ?? null);

  const isLive = live.status === 'en_vivo' && (!session || session.status === 'live' || session.status === 'reconnecting');
  const isReconnecting = session?.status === 'reconnecting';

  const handleCopy = () => {
    const link = `https://radiofrecuenciaweb.com/live/${live.id}`;
    navigator.clipboard.writeText(link).then(() => {
      SwalAlert.toast('Link copiado al portapapeles.', 'success');
    }).catch(() => {
      SwalAlert.toast('Error al copiar el link.', 'error');
    });
    onCopyLink(live);
  };

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden hover:border-background-300 transition-all">
      {/* Image area */}
      <div className="relative aspect-video w-full overflow-hidden bg-background-200">
        <img src={live.imageUrl} alt={live.title} className="w-full h-full object-cover object-top" />

        {/* ── OVERLAY: Loading (preparing / connecting) ── */}
        {session?.isLoading && (
          <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-3 z-10">
            <i className="ri-loader-4-line animate-spin text-background-50/90 text-3xl"></i>
            <div className="text-center px-4">
              <p className="text-background-50 text-sm font-bold">{session.statusMessage}</p>
              <p className="text-background-50/60 text-[10px] mt-1">
                {session.status === 'preparing'
                  ? 'Iniciando encoder y configurando el stream…'
                  : 'Verificando la señal entrante del locutor…'}
              </p>
            </div>
          </div>
        )}

        {/* ── OVERLAY: Señal perdida (reconnecting) ── */}
        {!session?.isLoading && isReconnecting && (
          <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center gap-3 z-10">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
              <i className="ri-signal-wifi-error-line text-amber-400 text-xl animate-pulse"></i>
            </div>
            <div className="text-center px-4">
              <p className="text-background-50 text-sm font-bold">Señal perdida</p>
              <p className="text-background-50/60 text-[10px] mt-1">
                Reconectando con la fuente del locutor…
              </p>
            </div>
          </div>
        )}

        {/* ── OVERLAY: Failed ── */}
        {session && !session.isLoading && !session.isStarting && session.status === 'failed' && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
            <div className="bg-red-50/95 backdrop-blur-sm rounded-xl px-4 py-3 max-w-[85%] text-center">
              <div className="w-8 h-8 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-2">
                <i className="ri-error-warning-line text-red-500 text-sm"></i>
              </div>
              <p className="text-red-700 text-[11px] font-semibold mb-2">{session.errorMessage || 'Error al iniciar'}</p>
              <button
                onClick={() => live.status === 'en_vivo' ? onFinalizar(live) : onIniciar(live)}
                className="px-3 py-1.5 rounded-lg bg-red-500 text-background-50 text-[10px] font-bold hover:bg-red-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line text-[10px] mr-1"></i>
                Reintentar
              </button>
            </div>
          </div>
        )}

        {/* ── Badge de sesión transicional (preparing/connecting/ending — sin overlay) ── */}
        {session && !session.isLoading && ['preparing', 'connecting', 'ending'].includes(session.status) && sessionColor && (
          <div className="absolute top-3 left-3 z-20">
            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold shadow-sm ${sessionColor.bg} ${sessionColor.text}`}>
              <span className={`w-2 h-2 rounded-full ${sessionColor.dot} animate-pulse`}></span>
              {session.statusMessage}
            </span>
          </div>
        )}

        {/* ── EN VIVO badge ── */}
        {isLive && !session?.isLoading && !isReconnecting && (
          <div className="absolute top-3 left-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-background-50 animate-pulse"></span>
            <span className="text-background-50 text-[10px] font-bold">EN VIVO</span>
            {isLive && session?.startedAt && (
              <span className="text-background-50/80 text-[10px] font-mono ml-1 border-l border-background-50/30 pl-1.5">
                {elapsed}
              </span>
            )}
          </div>
        )}

        {/* ── Badge de estado normal (no en vivo ni transitioning) ── */}
        {live.status !== 'en_vivo' && !session?.isLoading && !session?.isStarting && (
          <div
            className={`absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full text-[10px] font-semibold shadow-sm ${stColor.bg} ${stColor.text}`}
          >
            {statusLabels[live.status]}
          </div>
        )}

        {/* ── Provider badge ── */}
        <div className="absolute bottom-3 left-3 z-10">
          <span
            className={`px-2 py-0.5 rounded-md text-[9px] font-semibold flex items-center gap-1 backdrop-blur-sm ${provColor.bg} ${provColor.text}`}
          >
            <i className={`${providerIcons[live.provider]} text-[10px]`}></i>
            {providerLabels[live.provider]}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <h4 className="text-sm font-bold text-foreground-950 leading-tight line-clamp-2 mb-1.5">
          {live.title}
        </h4>
        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
          {live.description}
        </p>

        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[10px] text-foreground-400 mb-3">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-user-line text-[10px]"></i>
            {live.host}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-calendar-line text-[10px]"></i>
            {new Date(live.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-time-line text-[10px]"></i>
            {live.time}
          </span>
          {/* Timer extra en el body para live activo */}
          {isLive && session?.startedAt && (
            <span className="flex items-center gap-1 text-red-500 font-semibold whitespace-nowrap">
              <i className="ri-timer-line text-[10px]"></i>
              {elapsed}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 pt-3 border-t border-background-100 flex-wrap">
          {/* ── Bloqueo total durante loading ── */}
          {session?.isLoading ? (
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-semibold whitespace-nowrap">
              <i className="ri-loader-4-line animate-spin text-xs"></i>
              {session.statusMessage}
            </div>
          ) : session?.isStarting ? (
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-semibold whitespace-nowrap">
              <i className="ri-loader-4-line animate-spin text-xs"></i>
              {session.statusMessage}
            </div>
          ) : (
            <>
              {/* Iniciar */}
              {(live.status === 'programado' || live.status === 'scheduled' || (live.status === 'finalizado' && session && (session.status === 'completed' || session.status === 'failed'))) && (
                <button
                  onClick={() => onIniciar(live)}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-100 text-red-600 text-[11px] font-semibold cursor-pointer hover:bg-red-200 transition-colors whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-xs"></i>
                  Iniciar
                </button>
              )}
            </>
          )}

          {/* Finalizar (en_vivo): bloqueado en ending, disponible en reconnecting */}
          {live.status === 'en_vivo' && !session?.isLoading && (
            <button
              onClick={() => onFinalizar(live)}
              disabled={session?.status === 'ending'}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors whitespace-nowrap ${
                session?.status === 'ending'
                  ? 'bg-foreground-100 text-foreground-400 cursor-not-allowed'
                  : 'bg-red-100 text-red-600 cursor-pointer hover:bg-red-200'
              }`}
            >
              <i className={`${session?.status === 'ending' ? 'ri-loader-4-line animate-spin' : 'ri-stop-circle-line'} text-xs`}></i>
              {session?.status === 'ending' ? 'Finalizando…' : 'Finalizar'}
            </button>
          )}

          {/* Reconnecting: botón Finalizar sí disponible */}
          {isReconnecting && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-100 text-amber-700 text-[10px] font-semibold whitespace-nowrap">
              <i className="ri-signal-wifi-error-line text-[10px]"></i>
              Sin señal
            </span>
          )}

          {live.generatedRecordingId && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700 text-[10px] font-semibold whitespace-nowrap" title="Grabación disponible">
              <i className="ri-record-circle-line text-[10px]"></i>
              Grabación
            </span>
          )}
          {live.generatedVideoId && (
            <Link
              to="/admin/videos"
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-100 text-amber-700 text-[10px] font-semibold hover:bg-amber-200 transition-colors cursor-pointer whitespace-nowrap"
              title="Ver video"
            >
              <i className="ri-video-line text-[10px]"></i>
              Video
            </Link>
          )}
          {live.generatedPostId && (
            <Link
              to="/admin/posts"
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-sky-100 text-sky-700 text-[10px] font-semibold hover:bg-sky-200 transition-colors cursor-pointer whitespace-nowrap"
              title="Ver publicación"
            >
              <i className="ri-file-list-3-line text-[10px]"></i>
              Post
            </Link>
          )}
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[11px] font-semibold cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
          >
            <i className="ri-link text-xs"></i>
            Link
          </button>
          <button
            onClick={() => onEdit(live)}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer text-foreground-400 hover:bg-background-200 hover:text-foreground-600 transition-colors"
            title="Editar"
          >
            <i className="ri-pencil-line text-sm"></i>
          </button>
          {live.providerUrl && (
            <a
              href={live.providerUrl}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer text-foreground-400 hover:bg-primary-100 hover:text-primary-600 transition-colors"
              title="Ver en plataforma"
            >
              <i className="ri-external-link-line text-sm"></i>
            </a>
          )}
          <div className="flex-1"></div>
          {live.status !== 'en_vivo' && !session?.isLoading && !session?.isStarting && (
            <button
              onClick={() => onDelete(live)}
              className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer text-foreground-400 hover:bg-red-100 hover:text-red-600 transition-colors"
              title="Eliminar"
            >
              <i className="ri-delete-bin-line text-sm"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Mobile Card ─── */
function LiveCardMobile({
  live,
  onEdit,
  onDelete,
  onFinalizar,
  onCopyLink,
  onIniciar,
  isPremium,
  liveSessionStates,
}: {
  live: AdminLive;
  onEdit: (l: AdminLive) => void;
  onDelete: (l: AdminLive) => void;
  onFinalizar: (l: AdminLive) => void;
  onCopyLink: (l: AdminLive) => void;
  onIniciar: (l: AdminLive) => void;
  isPremium: boolean;
  liveSessionStates?: Record<number, LiveSessionState>;
}) {
  const stColor = statusColors[live.status];
  const provColor = providerColors[live.provider];
  const session = liveSessionStates?.[live.id];
  const sessionColor = session ? liveSessionStatusColor(session.status) : null;
  const elapsed = useElapsedTimer(session?.startedAt ?? null);

  const isLive = live.status === 'en_vivo' && (!session || session.status === 'live' || session.status === 'reconnecting');
  const isReconnecting = session?.status === 'reconnecting';

  const handleCopy = () => {
    const link = `https://radiofrecuenciaweb.com/live/${live.id}`;
    navigator.clipboard.writeText(link).then(() => {
      SwalAlert.toast('Link copiado al portapapeles.', 'success');
    }).catch(() => {
      SwalAlert.toast('Error al copiar el link.', 'error');
    });
    onCopyLink(live);
  };

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
      <div className="relative aspect-video w-full overflow-hidden bg-background-200">
        <img src={live.imageUrl} alt={live.title} className="w-full h-full object-cover object-top" />

        {/* ── OVERLAY: Loading ── */}
        {session?.isLoading && (
          <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-2 z-10">
            <i className="ri-loader-4-line animate-spin text-background-50/90 text-2xl"></i>
            <div className="text-center px-3">
              <p className="text-background-50 text-xs font-bold">{session.statusMessage}</p>
              <p className="text-background-50/60 text-[9px] mt-0.5">
                {session.status === 'preparing'
                  ? 'Iniciando encoder…'
                  : 'Verificando señal…'}
              </p>
            </div>
          </div>
        )}

        {/* ── OVERLAY: Señal perdida ── */}
        {!session?.isLoading && isReconnecting && (
          <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center gap-2 z-10">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <i className="ri-signal-wifi-error-line text-amber-400 text-base animate-pulse"></i>
            </div>
            <div className="text-center px-3">
              <p className="text-background-50 text-xs font-bold">Señal perdida</p>
              <p className="text-background-50/60 text-[9px] mt-0.5">Reconectando…</p>
            </div>
          </div>
        )}

        {/* ── OVERLAY: Failed ── */}
        {session && !session.isLoading && !session.isStarting && session.status === 'failed' && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
            <div className="bg-red-50/95 backdrop-blur-sm rounded-xl px-3 py-2 max-w-[90%] text-center">
              <div className="w-7 h-7 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-1.5">
                <i className="ri-error-warning-line text-red-500 text-xs"></i>
              </div>
              <p className="text-red-700 text-[10px] font-semibold mb-1.5">{session.errorMessage || 'Error al iniciar'}</p>
              <button
                onClick={() => live.status === 'en_vivo' ? onFinalizar(live) : onIniciar(live)}
                className="px-2.5 py-1 rounded-lg bg-red-500 text-background-50 text-[9px] font-bold hover:bg-red-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line text-[9px] mr-0.5"></i>
                Reintentar
              </button>
            </div>
          </div>
        )}

        {/* ── Badge de sesión transicional ── */}
        {session && !session.isLoading && ['preparing', 'connecting', 'ending'].includes(session.status) && sessionColor ? (
          <div className="absolute top-3 left-3 z-20">
            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold shadow-sm ${sessionColor.bg} ${sessionColor.text}`}>
              <span className={`w-2 h-2 rounded-full ${sessionColor.dot} animate-pulse`}></span>
              {session.statusMessage}
            </span>
          </div>
        ) : isLive && !session?.isLoading && !isReconnecting ? (
          <div className="absolute top-3 left-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-background-50 animate-pulse"></span>
            <span className="text-background-50 text-[10px] font-bold">EN VIVO</span>
            {isLive && session?.startedAt && (
              <span className="text-background-50/80 text-[10px] font-mono ml-0.5 border-l border-background-50/30 pl-1.5">
                {elapsed}
              </span>
            )}
          </div>
        ) : !session?.isLoading && !session?.isStarting ? (
          <div
            className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-semibold shadow-sm ${stColor.bg} ${stColor.text}`}
          >
            {statusLabels[live.status]}
          </div>
        ) : null}

        <div className="absolute bottom-3 left-3 z-10">
          <span
            className={`px-2 py-0.5 rounded-md text-[9px] font-semibold flex items-center gap-1 backdrop-blur-sm ${provColor.bg} ${provColor.text}`}
          >
            <i className={`${providerIcons[live.provider]} text-[10px]`}></i>
            {providerLabels[live.provider]}
          </span>
        </div>
      </div>

      <div className="p-3">
        <h4 className="text-sm font-bold text-foreground-950 leading-tight line-clamp-2 mb-1">{live.title}</h4>
        <p className="text-[11px] text-foreground-500 leading-relaxed line-clamp-2 mb-2">{live.description}</p>

        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[9px] text-foreground-400 mb-3">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-user-line text-[10px]"></i>
            {live.host}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-calendar-line text-[10px]"></i>
            {new Date(live.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-time-line text-[10px]"></i>
            {live.time}
          </span>
          {isLive && session?.startedAt && (
            <span className="flex items-center gap-1 text-red-500 font-semibold whitespace-nowrap">
              <i className="ri-timer-line text-[10px]"></i>
              {elapsed}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-background-100 flex-wrap">
          {session?.isLoading || session?.isStarting ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold whitespace-nowrap">
              <i className="ri-loader-4-line animate-spin text-xs"></i>
              {session.statusMessage}
            </div>
          ) : (
            <>
              {(live.status === 'programado' || live.status === 'scheduled' || (live.status === 'finalizado' && session && (session.status === 'completed' || session.status === 'failed'))) && (
                <button
                  onClick={() => onIniciar(live)}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-100 text-red-600 text-[10px] font-semibold cursor-pointer hover:bg-red-200 transition-colors whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-xs"></i>
                  Iniciar
                </button>
              )}
            </>
          )}
          {live.status === 'en_vivo' && !session?.isLoading && (
            <button
              onClick={() => onFinalizar(live)}
              disabled={session?.status === 'ending'}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-colors whitespace-nowrap ${
                session?.status === 'ending'
                  ? 'bg-foreground-100 text-foreground-400 cursor-not-allowed'
                  : 'bg-red-100 text-red-600 cursor-pointer hover:bg-red-200'
              }`}
            >
              <i className={`${session?.status === 'ending' ? 'ri-loader-4-line animate-spin' : 'ri-stop-circle-line'} text-xs`}></i>
              {session?.status === 'ending' ? 'Finalizando…' : 'Finalizar'}
            </button>
          )}
          {isReconnecting && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-100 text-amber-700 text-[9px] font-semibold whitespace-nowrap">
              <i className="ri-signal-wifi-error-line text-[9px]"></i>
              Sin señal
            </span>
          )}
          {live.generatedRecordingId && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700 text-[10px] font-semibold whitespace-nowrap">
              <i className="ri-record-circle-line text-[10px]"></i>
              Grab.
            </span>
          )}
          {live.generatedVideoId && (
            <Link
              to="/admin/videos"
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-100 text-amber-700 text-[10px] font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-video-line text-[10px]"></i>
              Video
            </Link>
          )}
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-semibold cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
          >
            <i className="ri-link text-xs"></i>
            Link
          </button>
          <button
            onClick={() => onEdit(live)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-semibold cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
          >
            <i className="ri-pencil-line text-xs"></i>
            Editar
          </button>
          {live.status !== 'en_vivo' && !session?.isLoading && !session?.isStarting && (
            <button
              onClick={() => onDelete(live)}
              className="ml-auto flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 text-red-500 text-[10px] font-semibold cursor-pointer hover:bg-red-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-delete-bin-line text-xs"></i>
              Eliminar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}